import React, { useState } from 'react';
import { AIValidatedInput, ValueStatus } from '../components/ui/AIValidatedInput';
import { AIValidatedInputWithHighlight } from '../components/ui/AIValidatedInputWithHighlight';

interface DemoField {
  id: string;
  label: string;
  value: string;
  status: ValueStatus;
  context?: string;
  highlightRanges?: Array<{ start: number; end: number; className?: string }>;
  extractionNote?: string;
  useHighlighting?: boolean;
}

export const AIInputDemoPage: React.FC = () => {
  const [demoFields, setDemoFields] = useState<DemoField[]>([
    {
      id: 'company-name',
      label: 'Company Name',
      value: 'Acme Corporation',
      status: 'unverified',
      context: 'Extracted from document header:\n\n"ACME CORPORATION\nAnnual Report 2023\n\nTo our valued shareholders and stakeholders,\n\nWe are pleased to present Acme Corporation\'s annual report for the fiscal year 2023..."',
      highlightRanges: [{ start: 33, end: 48 }], // "ACME CORPORATION"
      extractionNote: 'AI normalized "ACME CORPORATION" to proper case format',
      useHighlighting: true
    },
    {
      id: 'contract-value',
      label: 'Contract Value',
      value: '$150,000',
      status: 'verified',
      context: 'Found in section 3.2 Payment Terms:\n\n"The total contract value shall be One Hundred and Fifty Thousand Dollars ($150,000.00) payable in accordance with the payment schedule outlined in Exhibit A."',
      highlightRanges: [
        { start: 73, end: 125 } // "One Hundred and Fifty Thousand Dollars ($150,000.00)"
      ],
      extractionNote: 'AI converted written amount to numeric format and removed decimal places for whole numbers',
      useHighlighting: true
    },
    {
      id: 'phone',
      label: 'Phone Number',
      value: '(555) 123-4567',
      status: 'unverified',
      context: 'Contact information from page footer:\n\n"For questions regarding this contract, please contact us at contracts@company.com or call our legal department at five five five, one two three, four five six seven during business hours."',
      highlightRanges: [{ start: 142, end: 187 }], // "five five five, one two three, four five six seven"
      extractionNote: 'AI converted written phone number format to standard numeric format',
      useHighlighting: true
    },
    {
      id: 'email',
      label: 'Contact Email',
      value: 'contracts@wrongcompany.com',
      status: 'rejected',
      context: 'Extracted from footer:\n\n"For questions regarding this contract, please contact us at CONTRACTS@WRONGCOMPANY.COM or call our legal department at (555) 123-4567."',
      highlightRanges: [{ start: 87, end: 114 }], // "CONTRACTS@WRONGCOMPANY.COM"
      extractionNote: 'AI normalized email to lowercase format',
      useHighlighting: true
    },
    {
      id: 'manual-field',
      label: 'Manual Entry Field',
      value: '',
      status: 'manual'
    },
    {
      id: 'date',
      label: 'Contract Date',
      value: '2024-01-15',
      status: 'unverified',
      context: 'Found in contract header:\n\n"CONTRACT AGREEMENT\nEffective Date: January 15, 2024\nContract Number: ACM-2024-001\n\nThis agreement is entered into on the above date between..."',
      highlightRanges: [{ start: 49, end: 66 }], // "January 15, 2024"
      extractionNote: 'AI converted "January 15, 2024" to ISO format (YYYY-MM-DD)',
      useHighlighting: true
    }
  ]);

  const handleFieldChange = (fieldId: string, newValue: string, newStatus: ValueStatus) => {
    setDemoFields(fields => 
      fields.map(field => 
        field.id === fieldId 
          ? { ...field, value: newValue, status: newStatus }
          : field
      )
    );
  };

  const handleStatusChange = (fieldId: string, newStatus: ValueStatus) => {
    setDemoFields(fields => 
      fields.map(field => 
        field.id === fieldId 
          ? { ...field, status: newStatus }
          : field
      )
    );
  };

  const getStatusSummary = () => {
    const statusCounts = demoFields.reduce((acc, field) => {
      acc[field.status] = (acc[field.status] || 0) + 1;
      return acc;
    }, {} as Record<ValueStatus, number>);

    return statusCounts;
  };

  const statusSummary = getStatusSummary();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          AI Validated Input Demo
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Demonstration of AI-extracted value validation with human oversight and context highlighting
        </p>
      </div>

      {/* Status Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Validation Status Summary
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {statusSummary.unverified || 0}
            </div>
            <div className="text-sm text-orange-700 dark:text-orange-300">
              Unverified
            </div>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {statusSummary.verified || 0}
            </div>
            <div className="text-sm text-green-700 dark:text-green-300">
              Verified
            </div>
          </div>
          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {statusSummary.rejected || 0}
            </div>
            <div className="text-sm text-red-700 dark:text-red-300">
              Rejected
            </div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {statusSummary.manual || 0}
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Manual
            </div>
          </div>
        </div>
      </div>

      {/* Demo Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
          Contract Information Form
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {demoFields.map((field) => 
            field.useHighlighting ? (
              <AIValidatedInputWithHighlight
                key={field.id}
                id={field.id}
                label={field.label}
                value={field.value}
                initialValueStatus={field.status}
                valueContext={field.context}
                highlightRanges={field.highlightRanges}
                extractionNote={field.extractionNote}
                onChange={(value, status) => handleFieldChange(field.id, value, status)}
                onStatusChange={(status) => handleStatusChange(field.id, status)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            ) : (
              <AIValidatedInput
                key={field.id}
                id={field.id}
                label={field.label}
                value={field.value}
                initialValueStatus={field.status}
                valueContext={field.context}
                onChange={(value, status) => handleFieldChange(field.id, value, status)}
                onStatusChange={(status) => handleStatusChange(field.id, status)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            )
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
        <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">
          How to Use
        </h2>
        <div className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/40 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-orange-600 dark:text-orange-400 text-xs font-bold">1</span>
            </div>
            <div>
              <strong>Orange fields</strong> contain AI-extracted values that need verification. 
              Click the eye icon to see the source context.
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-green-600 dark:text-green-400 text-xs font-bold">2</span>
            </div>
            <div>
              <strong>Green fields</strong> have been verified and approved by a human reviewer.
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-red-600 dark:text-red-400 text-xs font-bold">3</span>
            </div>
            <div>
              <strong>Red fields</strong> contain AI extractions that have been rejected. 
              You can manually correct these values.
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-gray-600 dark:text-gray-400 text-xs font-bold">4</span>
            </div>
            <div>
              <strong>Gray fields</strong> are manually entered or have no AI-extracted values.
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-yellow-100 dark:bg-yellow-900/40 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-yellow-600 dark:text-yellow-400 text-xs font-bold">👁</span>
            </div>
            <div>
              <strong>Source highlighting:</strong> Yellow highlighting shows the source text that AI used 
              to extract the value. The final value may be normalized or transformed from this source.
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-blue-600 dark:text-blue-400 text-xs font-bold">ℹ</span>
            </div>
            <div>
              <strong>Extraction notes:</strong> Explain how AI transformed the source text into the final value 
              (e.g., normalization, format conversion, case changes).
            </div>
          </div>
        </div>
      </div>

      {/* Current Values Display */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Current Form State
        </h2>
        <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-x-auto">
          {JSON.stringify(
            demoFields.map(({ id, label, value, status }) => ({ id, label, value, status })),
            null,
            2
          )}
        </pre>
      </div>
    </div>
  );
}; 